/**
 * React Query hooks for Retail > Allocation.
 *
 * - useAllocationRuns()          GET  /v1/retail/allocation/runs (newest first)
 * - useAllocationRunDetail(id)   GET  /v1/retail/allocation/runs/{id} -> {run, lines}
 * - useAllocationStores()        GET  /v1/retail/allocation/stores (the run-time store picker list)
 * - useRunAllocation()           POST /v1/retail/allocation/runs body {week_ending?, store_nos?}
 * - downloadAllocationXlsx(id)   GET  /v1/retail/allocation/runs/{id}/export
 * - useAllocationInsights(id)    POST /v1/retail/allocation/runs/{id}/insights (202 + job id),
 *                                then polls GET /v1/retail/allocation/insights/{jobId};
 *                                also checks GET .../runs/{id}/insights/latest on mount to
 *                                resume an in-progress or recently finished question
 *
 * RBAC: retail.allocation.read (view runs/lines/export), retail.allocation.run
 * (trigger a new run). The API returns 403 when the caller lacks the read
 * capability; callers should render an AccessDenied state rather than crash.
 *
 * Filters (store, category, trend, stockout-only, constrained-only) are
 * applied client-side over the full line set for a run, mirroring the
 * useRetailStores.ts pattern. A run's universe is every active alloc store x
 * config item (a few thousand rows at most per CONTRACTS.md), small enough to
 * load in one shot and filter/sort in the browser.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from './api';
import { API_BASE } from './apiBase';

export const ALLOCATION_API_BASE = `${API_BASE}/v1/retail/allocation`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AllocationTrend = 'NO_SALES' | 'UP' | 'DOWN' | 'FLAT';

export type AllocationRun = {
  id: number;
  ran_at: string;
  ran_by: string | null;
  status: 'complete' | 'failed';
  week_ending: string | null;
  total_units: number | null;
  total_cost: number | null;
  store_count: number | null;
  item_count: number | null;
  stockout_count: number | null;
  constrained_item_count: number | null;
};

export type AllocationLine = {
  store_no: string;
  item_no: string;
  descr: string | null;
  category: string | null;
  subcategory: string | null;
  bop: number | null;
  sales_w3: number | null;
  sales_w2: number | null;
  sales_w1: number | null;
  sales_lw: number | null;
  sales_avg: number | null;
  wos: number | null;
  alpha_tier: string | null;
  tier_target: number | null;
  inner_pack: number | null;
  master_pack: number | null;
  store_priority: string | null;
  store_name: string | null;
  trend: AllocationTrend;
  suggested: number | null;
  final_alloc: number | null;
  stockout: boolean | number;
  unit_cost: number | null;
  ext_cost: number | null;
  wh_available: number | null;
  pulse_send_more: number | null;
  pulse_too_much: number | null;
  pulse_requested: number | null;
};

type RunsResponse = { runs: AllocationRun[] };
type RunDetailResponse = { run: AllocationRun; lines: AllocationLine[] };

export type AllocationInsightsJobStatus = 'running' | 'done' | 'error';

/**
 * A single poll of GET /v1/retail/allocation/insights/{jobId}. `answer` is
 * present once `status` is `done`; `error` is the backend's own plain-text
 * failure reason once `status` is `error`.
 */
export type AllocationInsightsJob = {
  status: AllocationInsightsJobStatus;
  answer?: string | null;
  model?: string | null;
  digest_generated_at?: string | null;
  error?: string | null;
};

type AllocationInsightsStartResponse = {
  job_id: string;
};

/** The shape returned by GET /v1/retail/allocation/runs/{id}/insights/latest. */
export type AllocationInsightsLatestJob = {
  job_id: number;
  question: string;
  status: AllocationInsightsJobStatus;
  answer: string | null;
  error: string | null;
  created_at: string;
};

type AllocationInsightsLatestResponse = { job: AllocationInsightsLatestJob | null };

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAllocationRuns() {
  const query = useQuery<RunsResponse, Error>({
    queryKey: ['allocation-runs'],
    queryFn: () => apiGet<RunsResponse>(`${ALLOCATION_API_BASE}/runs`),
    staleTime: 60_000,
    retry: false,
  });
  return {
    runs: query.data?.runs ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

export type AllocationSummaryRow = {
  category: string;
  subcategory: string;
  sku_count: number;
  active_sku_count: number;
  bop: number;
  sales_lw: number;
  sales_avg: number;
  trend: AllocationTrend;
  suggested: number;
  final_alloc: number;
  ext_cost: number;
  stockouts: number;
};

type RunSummaryResponse = { run: AllocationRun; rows: AllocationSummaryRow[] };

/**
 * The category/sub-category rollup behind the Allocation page (one row per
 * taxonomy pair across all stores, ~40 rows). The full store x SKU detail is
 * export-only; useAllocationRunDetail remains for anything that needs lines.
 */
export function useAllocationRunSummary(runId: number | null) {
  const query = useQuery<RunSummaryResponse, Error>({
    queryKey: ['allocation-run-summary', runId],
    queryFn: () =>
      apiGet<RunSummaryResponse>(`${ALLOCATION_API_BASE}/runs/${encodeURIComponent(String(runId))}/summary`),
    enabled: runId != null,
    staleTime: 60_000,
    retry: false,
  });
  return {
    run: query.data?.run ?? null,
    rows: query.data?.rows ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useAllocationRunDetail(runId: number | null) {
  const query = useQuery<RunDetailResponse, Error>({
    queryKey: ['allocation-run-detail', runId],
    queryFn: () =>
      apiGet<RunDetailResponse>(`${ALLOCATION_API_BASE}/runs/${encodeURIComponent(String(runId))}`),
    enabled: runId != null,
    staleTime: 60_000,
    retry: false,
  });
  return {
    run: query.data?.run ?? null,
    lines: query.data?.lines ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** One store the operator can choose to include in a run. */
export type AllocationStore = {
  store_no: string;
  store_name: string | null;
  alpha_tier: string | null;
  alloc_priority: string | null;
};

/**
 * The stores a run may include, which is the list the store picker shows.
 * Served by the api from the same rule the allocation engine applies, so the
 * picker cannot offer a store the run would then refuse.
 */
export function useAllocationStores(enabled = true) {
  const query = useQuery<{ stores: AllocationStore[] }, Error>({
    queryKey: ['allocation-stores'],
    queryFn: () => apiGet<{ stores: AllocationStore[] }>(`${ALLOCATION_API_BASE}/stores`),
    enabled,
    staleTime: 300_000,
    retry: false,
  });
  return {
    stores: query.data?.stores ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Turn the Run allocation dialog's store choice into what the request needs and
 * what the operator is told (ISD-362).
 *
 * `wholeFleet` true means send no store list at all, which the api reads as
 * every store on the allocation list. Picking stores sends exactly those.
 * `canRun` is false when the operator chose to pick stores and then picked
 * none, because an empty list would silently run the whole fleet instead.
 */
export function resolveRunStoreScope(args: {
  wholeFleet: boolean;
  picked: string[];
  eligibleCount: number;
}): { store_nos: string[]; canRun: boolean; label: string } {
  if (args.wholeFleet) {
    return {
      store_nos: [],
      canRun: true,
      label:
        args.eligibleCount > 0
          ? `all ${args.eligibleCount} stores`
          : 'every store on the allocation list',
    };
  }
  const store_nos = [...new Set(args.picked)];
  return {
    store_nos,
    canRun: store_nos.length > 0,
    label: `${store_nos.length} ${store_nos.length === 1 ? 'store' : 'stores'}`,
  };
}

export function useRunAllocation() {
  const qc = useQueryClient();
  return useMutation<
    { run: AllocationRun },
    Error,
    { week_ending?: string; store_nos?: string[] } | void
  >({
    mutationFn: (body) => apiPost<{ run: AllocationRun }>(`${ALLOCATION_API_BASE}/runs`, body ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['allocation-runs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Allocation insights: fire-and-poll
// ---------------------------------------------------------------------------
//
// Asking a question no longer holds a request open waiting for Claude to
// answer. POST /v1/retail/allocation/runs/{id}/insights now returns 202 with
// a job id right away, and the answer shows up later at
// GET /v1/retail/allocation/insights/{jobId}. The AI analyst's answer can
// take longer than a browser (and any load balancer in front of it) is
// willing to hold a connection open for, which is what broke the old
// request-response version for a user (api#547).

/** How often the panel checks on a running job. */
export const ALLOCATION_INSIGHTS_POLL_MS = 2_000;

/** How long the panel keeps checking before it gives up on a job. */
export const ALLOCATION_INSIGHTS_TIMEOUT_MS = 3 * 60_000;

/**
 * Shown when the ask or poll request itself fails, such as a network drop or
 * a 5xx response. This never surfaces the raw "API <status> on <path>"
 * string that apiFetch throws internally, only a message the operator can
 * act on.
 */
export const ALLOCATION_INSIGHTS_REQUEST_FAILED_MESSAGE = 'The question could not be answered. Try again.';

/** Shown when a job never reaches done or error before the timeout ceiling. */
export const ALLOCATION_INSIGHTS_TIMEOUT_MESSAGE = 'This answer is taking unusually long. Try again in a bit.';

/**
 * react-query refetchInterval callback for the job-status poll. Keeps
 * polling every ALLOCATION_INSIGHTS_POLL_MS while the job is running (or
 * before the first poll has come back), stops once it lands on done or
 * error so the panel does not keep hitting a finished job.
 */
export function nextInsightsPollDelay(status: AllocationInsightsJobStatus | undefined): number | false {
  return status === 'done' || status === 'error' ? false : ALLOCATION_INSIGHTS_POLL_MS;
}

/**
 * Picks the message to show for a failed question, in priority order: a
 * timeout beats a job-level failure beats a request-level failure. Job-level
 * failures (the job ran but the analysis itself failed) show the backend's
 * own plain message. Request-level failures (the ask or poll call could not
 * even complete) get a generic, actionable message instead of the raw fetch
 * error string.
 */
export function resolveInsightsErrorMessage(input: {
  requestFailed: boolean;
  jobError: string | null | undefined;
  timedOut: boolean;
}): string | null {
  if (input.timedOut) return ALLOCATION_INSIGHTS_TIMEOUT_MESSAGE;
  if (input.jobError) return input.jobError;
  if (input.requestFailed) return ALLOCATION_INSIGHTS_REQUEST_FAILED_MESSAGE;
  return null;
}

/**
 * Decides whether the job returned by GET .../insights/latest should become
 * the hook's active job. Pure so the resume decision is unit testable
 * without rendering the hook.
 *
 * Returns null (nothing to adopt) when there is no latest job, or when a
 * job is already active locally: a question asked in this session, whether
 * before or after the latest-job lookup resolves, always wins over a
 * resumed one.
 */
export function resolveResumedJob(
  latestJob: AllocationInsightsLatestJob | null | undefined,
  currentJobId: string | null,
): { jobId: string; startedAtMs: number; question: string } | null {
  if (!latestJob || currentJobId != null) return null;
  return {
    jobId: String(latestJob.job_id),
    startedAtMs: new Date(latestJob.created_at).getTime(),
    question: latestJob.question,
  };
}

export type AllocationInsightsPhase = 'idle' | 'thinking' | 'done' | 'error';

export type UseAllocationInsightsResult = {
  phase: AllocationInsightsPhase;
  answer: string | null;
  errorMessage: string | null;
  /**
   * The question behind the job currently shown, whether it was just typed
   * in this session or the panel picked it back up from
   * GET .../insights/latest after a remount. Null while idle.
   */
  question: string | null;
  ask: (question: string) => void;
  reset: () => void;
};

/**
 * Asks the AI analyst a question about a run and polls for the answer.
 * `ask` starts the job (POST .../insights, 202 + job id). The hook then
 * polls GET .../insights/{jobId} every ALLOCATION_INSIGHTS_POLL_MS until the
 * job is done or errors, or until ALLOCATION_INSIGHTS_TIMEOUT_MS passes with
 * no answer, at which point it gives up rather than polling forever.
 *
 * On mount, and again whenever runId changes, the hook also checks
 * GET .../runs/{id}/insights/latest for the caller's own most recent job on
 * that run. Someone who asks a question, then leaves the Allocation page
 * rather than watch a spinner, comes back to the same thinking state or the
 * finished answer instead of a blank panel, because the work happens in a
 * background worker and only the screen forgot about it. Asking a new
 * question always replaces whatever is shown, resumed or not.
 *
 * The runId is passed in at the hook level (not at ask time) because a fresh
 * run means a prior job's answer no longer applies to what is on screen, so
 * the hook clears its own state on every runId change before checking for
 * something to resume on the new run.
 */
export function useAllocationInsights(runId: number | null): UseAllocationInsightsResult {
  const [jobId, setJobId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [question, setQuestion] = useState<string | null>(null);

  const startJob = useMutation<AllocationInsightsStartResponse, Error, { question: string }>({
    mutationFn: ({ question: q }) => {
      if (runId == null) throw new Error('No run selected.');
      return apiPost<AllocationInsightsStartResponse>(
        `${ALLOCATION_API_BASE}/runs/${encodeURIComponent(String(runId))}/insights`,
        { question: q },
      );
    },
    onSuccess: (data, variables) => {
      setTimedOut(false);
      setStartedAt(Date.now());
      setJobId(data.job_id);
      setQuestion(variables.question);
    },
  });

  // A new run means any prior job no longer applies: clear local state so a
  // stale answer never shows next to a different run's KPIs and grid, before
  // the latest-job lookup below has a chance to resume something for the run
  // just selected.
  useEffect(() => {
    setJobId(null);
    setStartedAt(null);
    setTimedOut(false);
    setQuestion(null);
    startJob.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const latestQuery = useQuery<AllocationInsightsLatestResponse, Error>({
    queryKey: ['allocation-insights-latest', runId],
    queryFn: () =>
      apiGet<AllocationInsightsLatestResponse>(
        `${ALLOCATION_API_BASE}/runs/${encodeURIComponent(String(runId))}/insights/latest`,
      ),
    enabled: runId != null,
    staleTime: 0,
    retry: false,
  });

  // Adopt whatever the latest-job lookup found, per resolveResumedJob: a
  // question the user asks after mount, before this resolves, wins over a
  // resumed one rather than being overwritten by it.
  useEffect(() => {
    const resumed = resolveResumedJob(latestQuery.data?.job, jobId);
    if (!resumed) return;
    setJobId(resumed.jobId);
    setStartedAt(resumed.startedAtMs);
    setQuestion(resumed.question);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestQuery.data]);

  const jobQuery = useQuery<AllocationInsightsJob, Error>({
    queryKey: ['allocation-insights-job', jobId],
    queryFn: () => apiGet<AllocationInsightsJob>(`${ALLOCATION_API_BASE}/insights/${encodeURIComponent(String(jobId))}`),
    enabled: jobId != null && !timedOut,
    refetchInterval: (query) => nextInsightsPollDelay(query.state.data?.status),
    refetchIntervalInBackground: false,
    retry: 2,
    staleTime: 0,
  });

  // A ref so the timeout callback below always sees the latest status
  // without needing to reschedule itself on every poll tick.
  const latestStatus = useRef<AllocationInsightsJobStatus | undefined>(undefined);
  latestStatus.current = jobQuery.data?.status;

  // Give up after a sensible ceiling instead of polling forever. One timer
  // per job: it fires once, and only turns into a user-visible timeout if
  // the job has not already resolved by then.
  useEffect(() => {
    if (jobId == null || startedAt == null) return undefined;
    const timer = setTimeout(() => {
      if (latestStatus.current !== 'done' && latestStatus.current !== 'error') {
        setTimedOut(true);
      }
    }, ALLOCATION_INSIGHTS_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [jobId, startedAt]);

  function ask(question: string) {
    startJob.mutate({ question });
  }

  function reset() {
    setJobId(null);
    setStartedAt(null);
    setTimedOut(false);
    setQuestion(null);
    startJob.reset();
  }

  const job = jobQuery.data;
  const errorMessage = resolveInsightsErrorMessage({
    requestFailed: startJob.isError || jobQuery.isError,
    jobError: job?.status === 'error' ? job.error : null,
    timedOut,
  });

  let phase: AllocationInsightsPhase;
  if (startJob.isPending) {
    phase = 'thinking';
  } else if (errorMessage != null) {
    phase = 'error';
  } else if (job?.status === 'done') {
    phase = 'done';
  } else if (jobId != null) {
    phase = 'thinking';
  } else {
    phase = 'idle';
  }

  return {
    phase,
    answer: job?.status === 'done' ? job.answer ?? null : null,
    errorMessage,
    question,
    ask,
    reset,
  };
}

/**
 * Triggers a browser download of a run's Excel export.
 * Calls GET /v1/retail/allocation/runs/{id}/export and saves the blob.
 * Mirrors downloadRetailXlsx / downloadOrdersXlsx.
 */
export async function downloadAllocationXlsx(runId: number): Promise<void> {
  const url = `${ALLOCATION_API_BASE}/runs/${encodeURIComponent(String(runId))}/export`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? `allocation-run-${runId}.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// RBAC
// ---------------------------------------------------------------------------

// Binary access-grant model (spec 00_access-binary-grants.md, 2026-08-09):
// Allocation is one tool, one accessSlug (stores.allocation), no separate
// read/run levels — holding it grants both viewing and running an allocation.
// Replaces the old retail.allocation.read / .run pair.
export function hasAllocationRead(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('stores.allocation');
}

export function hasAllocationRun(perms: string[] | undefined, superAdmin = false): boolean {
  return hasAllocationRead(perms, superAdmin);
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** True when a stockout flag is truthy, handling both 1/0 and boolean API shapes. */
export function isStockout(line: Pick<AllocationLine, 'stockout'>): boolean {
  return line.stockout === 1 || line.stockout === true;
}

/**
 * True when proration reduced this line's allocation below what was
 * suggested (the item's warehouse supply could not cover every store's
 * suggested units). Mirrors the run summary's constrained_item_count logic.
 */
export function isConstrained(line: Pick<AllocationLine, 'suggested' | 'final_alloc'>): boolean {
  const suggested = line.suggested ?? 0;
  const final = line.final_alloc ?? 0;
  return suggested > 0 && final < suggested - 0.005;
}

/** Formats a unit quantity with no decimals: "1,240". Null/NaN render "--". */
export function formatUnits(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v);
}

/** Formats a dollar amount as "$1,234.56". Null/NaN render "--". */
export function formatAllocCost(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

/** Formats weeks-of-supply to one decimal: "2.3". Null/NaN render "--". */
export function formatWos(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  return v.toFixed(1);
}

export function trendLabel(t: AllocationTrend): string {
  switch (t) {
    case 'UP':        return 'Up';
    case 'DOWN':       return 'Down';
    case 'FLAT':       return 'Flat';
    case 'NO_SALES':   return 'No sales';
  }
}

export const TREND_TONES: Record<AllocationTrend, string> = {
  UP:        'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  DOWN:      'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  FLAT:      'bg-amp-cream-warm text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink',
  NO_SALES:  'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
};
